import { useNuxtApp, useRequestURL } from '#imports'
import type { RouteRecordRaw, RouterScrollBehavior } from 'vue-router'

import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { toBcp47Locale } from '@backmarket/utils/string/toBcp47Locale'
import { urlHashToObject } from '@backmarket/utils/url/urlHashToObject'
import type { RouterConfig } from 'nuxt/schema'

import adminRoutes from '~/scopes/admin/routes'
import authRoutes from '~/scopes/auth/routes'
import buybackRoutes from '~/scopes/buyback/routes'
import campaignRoutes from '~/scopes/campaign/routes'
import checkoutRoutes from '~/scopes/checkout/routes'
import cmsRoutes from '~/scopes/cms/routes'
import customerCareRoutes from '~/scopes/customer-care/routes'
import customerConversationRoutes from '~/scopes/customer-conversation/routes'
import customerDeliveryRoutes from '~/scopes/customer-delivery/routes'
import customerTestingReportsRoutes from '~/scopes/customer-testing-reports/routes'
import dashboardRoutes from '~/scopes/dashboard/routes'
import discountRoutes from '~/scopes/discount/routes'
import giftCardsRoutes from '~/scopes/gift-cards/routes'
import guestRoutes from '~/scopes/guest/routes'
import helpCenterRoutes from '~/scopes/help-center/routes'
import homeRoutes from '~/scopes/home/routes'
import insuranceRoutes from '~/scopes/insurance/routes'
import legalRoutes from '~/scopes/legal/routes'
import mcpRoutes from '~/scopes/mcp/routes'
import modelsComparisonRoutes from '~/scopes/models-comparison/routes'
import outletRoutes from '~/scopes/outlet/routes'
import partnershipsRoutes from '~/scopes/partnerships/routes'
import priceGuideRoutes from '~/scopes/price-guide/routes'
import productRoutes from '~/scopes/product-core/routes'
import productListRoutes from '~/scopes/product-list/routes'
import recyclingRoutes from '~/scopes/recycling/routes'
import reviewsRoutes from '~/scopes/reviews/routes'
import shoppingAssistantRoutes from '~/scopes/shopping-assistant/routes'

// Plain in-page anchors (e.g. the condensed PDP tab sections). The CMS uses
// `#scroll=false&target=...` hashes, which are handled elsewhere and excluded
// here via the `=` check.
function isAnchorHash(hash?: string): boolean {
  return Boolean(
    hash && hash.startsWith('#') && hash.length > 1 && !hash.includes('='),
  )
}

// Keep the anchor target clear of the sticky bar, using the section's own
// `scroll-margin-top` (vue-router ignores it, so we pass it as an offset).
function getHashScrollOffset(hash: string): number {
  if (!import.meta.client) return 0

  const el = document.getElementById(decodeURIComponent(hash.slice(1)))
  if (!el) return 0

  return Number.parseFloat(getComputedStyle(el).scrollMarginTop) || 0
}

// Function to control scrolling when navigating between pages.
// If a falsy value or an empty object is returned, no scrolling will happen.
export const scrollBehavior: RouterScrollBehavior = (
  to,
  from,
  savedPosition,
) => {
  // Returning the savedPosition will result in a native-like behavior when navigating with back/forward buttons:
  if (savedPosition) return savedPosition

  // We don't want to scroll if scroll hash param is specified to 'false'
  const hash: Record<string, unknown[]> = urlHashToObject(to.hash)
  if (hash.scroll?.[0] === 'false') return false

  // We don't want to scroll if user navigate within the same page
  if (from.path === to.path) return false

  // Opt in per route so in-page anchor scrolling doesn't affect other pages.
  const isAnchor = to.meta.scrollToAnchor === true && isAnchorHash(to.hash)

  // We want to scroll as soon as page finished to load (when async data are fetched and ready)
  return new Promise((resolve) => {
    useNuxtApp().hooks.hookOnce('page:finish', () => {
      if (isAnchor) {
        // Deep link to a section: scroll to it (offset under the sticky bar)
        // instead of snapping back to the top.
        resolve({
          el: to.hash,
          top: getHashScrollOffset(to.hash),
          behavior: 'smooth',
        })
        return
      }

      resolve({
        top: 0,
      })
    })
  })
}

// https://router.vuejs.org/api/interfaces/routeroptions.html
export default <RouterConfig>{
  routes: (fileSystemRoutes): readonly RouteRecordRaw[] => {
    const { market } = useMarketplace()
    const requestPath = useRequestURL().pathname
    const marketLocales = market.locales.map((locale) => toBcp47Locale(locale))
    const defaultLocale = toBcp47Locale(market.defaultLocale)
    const locales = marketLocales.join('|')
    const currentLocale =
      marketLocales.find((l) => requestPath.startsWith(`/${l}`)) ??
      defaultLocale

    return [
      ...fileSystemRoutes,
      { path: '/', redirect: `/${defaultLocale}` },
      {
        path: `/:locale(${locales})`,
        component: () =>
          import('@backmarket/nuxt-layer-base/components/RouteRecordWithChildrenOnly/RouteRecordWithChildrenOnly.vue'),
        children: [
          ...reviewsRoutes(),
          ...buybackRoutes(),
          ...cmsRoutes({ market }),
          ...campaignRoutes(),
          ...customerCareRoutes(),
          ...customerConversationRoutes(),
          ...customerDeliveryRoutes(),
          ...customerTestingReportsRoutes(),
          ...helpCenterRoutes(),
          ...homeRoutes(),
          ...legalRoutes(),
          ...mcpRoutes(),
          ...modelsComparisonRoutes(),
          ...outletRoutes(),
          ...priceGuideRoutes(),
          ...productRoutes(),
          ...productListRoutes({ locale: currentLocale }),
          ...dashboardRoutes(),
          ...checkoutRoutes(),
          ...insuranceRoutes,
          ...recyclingRoutes,
          ...discountRoutes(),
          ...giftCardsRoutes(),
          ...guestRoutes(),
          ...shoppingAssistantRoutes(),
          ...partnershipsRoutes(),
        ],
      },
      ...authRoutes(),
      ...adminRoutes,
      // temporary work-around to avoid 404 in links from CMS pages since they transform links into relative links
      // to be removed once the smart RouterLinkComponent PR is merged
      {
        path: '/ValeriesEntireLife',
        component: () =>
          import('@backmarket/nuxt-layer-base/components/ExternalPagePlaceholder/ExternalPagePlaceholder.vue'),
        meta: {
          disableClientSideNavigation: () => true,
        },
      },
    ]
  },
  scrollBehavior,
}
